import { Check, ListChecks, MessageSquarePlus } from 'lucide-react'
import type { ReminderItem } from '../../../hooks/reminder-mapping'
import type { StagedAction } from '../../../hooks/staged-actions/types'
import type { TranscriptJump } from '../../../components/meeting/transcript'
import { useMeetingActionItems } from '../action-items/use-meeting-action-items'
import { useMeetingReminderActions } from '../action-items/use-meeting-reminder-actions'
import { Button } from '@highlight/ui'
import { useActionRowModel } from '../../../hooks/staged-actions/use-action-row-model'
import { getActionIcon, getActionTitle } from '../../../utils/activity-helpers'
import { sendReadiness } from '../action-items/send-readiness'
import { ConfidenceBar } from '../../../components/confidence-bar'

interface MeetingCapturedActionsProps {
  audioSessionId: string | undefined
  /** A saved meeting's note, which its final planner run often files its actions under. */
  audioNoteId?: string
  /** Only the live transcript can jump; a saved meeting shows each action's time as plain text. */
  onJump?: (jump: TranscriptJump) => void
}

/**
 * What planner v2 caught in the meeting, the actions it staged and the reminders
 * it wrote, each linking back to where it was said while it's live.
 */
export function MeetingCapturedActions({ audioSessionId, audioNoteId, onJump }: MeetingCapturedActionsProps) {
  const { items } = useMeetingActionItems(audioSessionId, audioNoteId)
  if (items.length === 0) return null

  return (
    // Sticky: the live transcript pins its scroll to the bottom, which would otherwise carry this out of view
    <section
      aria-label="Captured action items"
      className="bg-background sticky top-0 z-10 -mx-5 mb-2 flex max-h-[40vh] flex-col gap-1 overflow-y-auto border-b px-3 pb-3 pt-2"
    >
      <h2 className="type-ui-sm text-foreground-secondary flex items-center gap-1.5 px-2 py-1 font-medium">
        Captured action items
        <span className="text-foreground-placeholder tabular-nums">{items.length}</span>
      </h2>
      <ul className="flex flex-col">
        {items.map((item) =>
          item.kind === 'reminder' ? (
            <CapturedReminderRow key={item.id} reminder={item.reminder} onJump={onJump} />
          ) : (
            <CapturedActionRow key={item.id} action={item.action} onJump={onJump} />
          ),
        )}
      </ul>
    </section>
  )
}

const ROW_CLASSNAME =
  'hover:bg-background-subtle corner-smooth flex items-center gap-3 rounded-xl px-2 py-2 transition-colors'
const ROW_TITLE_CLASSNAME =
  'type-ui-sm text-foreground min-w-0 cursor-pointer truncate bg-transparent p-0 text-left font-medium'

const SENT_STATUSES = new Set(['pending', 'completed', 'undo-error'])

/**
 * One action like the notch shows it: its integration, its title (opening the
 * action), how sure the planner was, and Send.
 */
function CapturedActionRow({ action, onJump }: { action: StagedAction; onJump?: (jump: TranscriptJump) => void }) {
  const row = useActionRowModel(action, { sourceLabel: 'Meeting', eventSource: 'meeting-captured' })
  const readiness = sendReadiness(action)
  // Only a message or invite sends; a chat or ticket opens from its title instead
  const isSent = readiness !== undefined && SENT_STATUSES.has(row.status)
  const canSend =
    readiness !== undefined &&
    !isSent &&
    (row.isStaged || row.isRunning || row.actionHasError) &&
    ((readiness.missing.length === 0 && readiness.drafted) || !!row.unsatisfiedPrerequisite)
  // The planner run's trigger is the transcript point it acted on; createdAt trails it
  const time = action.sourceMetadata?.plannerTriggeredAt ?? action.createdAt

  return (
    <li className={ROW_CLASSNAME}>
      {/* An app logo fills its box, so the icon's rounded mask would clip its corners */}
      <span className="flex size-5 shrink-0 items-center justify-center [&>img]:rounded-none">
        {getActionIcon(action, 18)}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <button type="button" onClick={() => row.openActionView()} className={ROW_TITLE_CLASSNAME}>
          {getActionTitle(action)}
        </button>
        <CapturedItemTime time={time} onJump={onJump} />
      </div>
      <ConfidenceBar action={action} className="text-foreground-secondary" />
      {isSent ? (
        <span className="type-ui-sm text-foreground-secondary flex shrink-0 items-center gap-1">
          <Check aria-hidden="true" className="text-success size-4" />
          Sent
        </span>
      ) : (
        canSend && (
          <Button
            size="sm"
            variant={row.actionHasError ? 'outline' : 'secondary'}
            tone={row.actionHasError ? 'danger' : undefined}
            className="shrink-0"
            disabled={row.isRunning}
            loading={row.isRunning}
            onClick={() => row.handleInvoke('meeting-captured-send')}
          >
            {row.actionHasError ? 'Retry' : row.unsatisfiedPrerequisite ? row.ctaLabel : 'Send'}
          </Button>
        )
      )}
    </li>
  )
}

/** A reminder the meeting wrote. Its title opens it in the Reminders view, and Chat starts a chat about it. */
function CapturedReminderRow({
  reminder,
  onJump,
}: {
  reminder: ReminderItem
  onJump?: (jump: TranscriptJump) => void
}) {
  const { openReminder, startChat } = useMeetingReminderActions(reminder, 'meeting-captured')

  return (
    <li className={ROW_CLASSNAME} data-reminder-id={reminder.id}>
      <span className="text-foreground-secondary flex size-5 shrink-0 items-center justify-center">
        <ListChecks aria-hidden="true" className="size-4" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <button type="button" onClick={openReminder} className={ROW_TITLE_CLASSNAME}>
          {reminder.title}
        </button>
        <CapturedItemTime time={reminder.createdAt.toISOString()} onJump={onJump} />
      </div>
      <Button
        size="sm"
        variant="secondary"
        className="shrink-0"
        left={<MessageSquarePlus />}
        aria-label={`Start a chat about ${reminder.title}`}
        onClick={startChat}
      >
        Chat
      </Button>
    </li>
  )
}

/** When the item was caught. It links to that point in the live transcript, and is plain text once the meeting is saved. */
function CapturedItemTime({ time, onJump }: { time: string; onJump?: (jump: TranscriptJump) => void }) {
  const displayTime = new Date(time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  if (!onJump) return <span className="type-ui-xs text-foreground-placeholder tabular-nums">{displayTime}</span>

  return (
    <button
      type="button"
      onClick={() => onJump({ time })}
      className="type-ui-xs text-foreground-placeholder hover:text-foreground-secondary w-fit cursor-pointer bg-transparent p-0 tabular-nums"
      aria-label={`Show the transcript at ${displayTime}`}
    >
      {displayTime}
    </button>
  )
}
